import React, { useEffect, useRef } from 'react';

import { useMultiMacroQueryStoreHook } from '@confluence/fabric-extension-queries/entry-points/multiMacroQuery.ts';
import { queryStaticMacro } from '@confluence/fabric-extension-queries/entry-points/singleStaticMacroQuery.ts';
import { fg } from '@confluence/feature-gating';
import { getLogger } from '@confluence/logger';

import {
	StaticMacroStoreProvider,
	type StaticMacroAttempt,
	useStaticMacroStoreContext,
} from './StaticMacroStoreContext.tsx';

const logger = getLogger('StaticMacroDataBridge');

// If a refetch brings back no HTML, the backend may still be rendering the new macro state.
export const STATIC_MACRO_REFETCH_RETRY_DELAY_MS = 30_000;

export type DraftSyncCompletedHandler = (syncStartedAt?: number) => void;

/**
 * This loads the multi-macro query store into the static macro store context.
 *
 * Sits in `MultiMacroQueryContainer` and `StaticMacroStoreProvider`, reads `staticMacroData` from the query store
 * and registers entries into the static macro context. Each `ForgeMacro` is only notified when its own data arrives.
 * When all staticMacros pages have been fetched, calls `markFullyLoaded` so pending macros know to fall back to the iframe renderer.
 *
 * Also wires up `ctx.triggerRefetch` (via `setRefetchTrigger`) to the single-macro
 * `confluence_staticMacro` query, so an individual `ForgeMacro` instance can request fresh static
 * HTML for itself without re-fetching/re-paginating every macro on the page.
 */
export function StaticMacroDataBridge({
	draftSyncCompletedRef,
	requestDraftSync,
	expectedContentId,
}: {
	draftSyncCompletedRef?: React.MutableRefObject<DraftSyncCompletedHandler | null>;
	requestDraftSync?: () => void;
	expectedContentId?: string;
}): null {
	const ctx = useStaticMacroStoreContext();
	const [{ staticMacroData, staticMacrosComplete, contentId, cloudId, mode }] =
		useMultiMacroQueryStoreHook();

	// List of all currently registered localIds so we don't do duplicate work if we received a localId multiple times.
	const registeredLocalIds = useRef<Set<string>>(new Set());
	const prevStaticMacroPagesRef = useRef<Record<string, any> | null>(null);
	const fullyLoadedRef = useRef(false);
	const registeredForContentIdRef = useRef(expectedContentId);

	useEffect(() => {
		if (!ctx || !draftSyncCompletedRef) {
			return undefined;
		}
		draftSyncCompletedRef.current = ctx.notifyDraftSyncCompleted;
		return () => {
			draftSyncCompletedRef.current = null;
		};
	}, [ctx, draftSyncCompletedRef]);

	useEffect(() => {
		if (!ctx || !requestDraftSync) {
			return undefined;
		}
		ctx.setDraftSyncRequester(requestDraftSync);
		return () => {
			ctx.setDraftSyncRequester(null);
		};
	}, [ctx, requestDraftSync]);

	useEffect(() => {
		if (!ctx || !contentId) {
			return undefined;
		}

		const pendingRetries = new Map<string, ReturnType<typeof setTimeout>>();

		ctx.setRefetchTrigger((localId: string, attempt: StaticMacroAttempt) => {
			// A newer refetch for this macro supersedes a retry that hasn't fired yet.
			clearTimeout(pendingRetries.get(localId));
			pendingRetries.delete(localId);

			const scheduleRetry = () => {
				pendingRetries.set(
					localId,
					setTimeout(() => {
						pendingRetries.delete(localId);
						fetchStaticMacro(true);
					}, STATIC_MACRO_REFETCH_RETRY_DELAY_MS),
				);
			};

			const fetchStaticMacro = (isRetry: boolean) => {
				void queryStaticMacro({ contentId, cloudId, localId, mode })
					.then((result) => {
						if (result?.value) {
							ctx.register(localId, result.value, { force: true, attempt });
						} else if (result?.errorCode) {
							ctx.registerError(localId, result.errorCode, { force: true, attempt });
						} else if (isRetry) {
							ctx.notifyRefetchExhausted(localId, attempt);
						} else {
							scheduleRetry();
						}
					})
					.catch((error) => {
						if (!isRetry) {
							scheduleRetry();
							return;
						}
						logger.error`Static macro refetch failed after retry for localId ${localId} on content ${contentId}: ${error}`;
						ctx.registerError(localId, 'REFETCH_FAILED', { force: true, attempt });
					});
			};

			fetchStaticMacro(false);
		});

		return () => {
			pendingRetries.forEach(clearTimeout);
			ctx.setRefetchTrigger(null);
		};
	}, [ctx, contentId, cloudId, mode]);

	useEffect(() => {
		if (!ctx) return;

		// eslint-disable-next-line confluence-feature-gating/no-new-feature-gates -- Existing gate retained during CCP-9677 lint-rule rollout; migration deferred.
		if (expectedContentId !== undefined && fg('conf_frontend_static_macro_perf')) {
			if (registeredForContentIdRef.current !== expectedContentId) {
				registeredForContentIdRef.current = expectedContentId;
				registeredLocalIds.current = new Set();
				prevStaticMacroPagesRef.current = null;
				fullyLoadedRef.current = false;
			}
			// The query store still holds the previous page's results, including `staticMacrosComplete`.
			if (contentId !== expectedContentId) return;
		}

		if (staticMacroData !== prevStaticMacroPagesRef.current) {
			prevStaticMacroPagesRef.current = staticMacroData;

			for (const node of Object.values(staticMacroData ?? {})) {
				const { localId, value, error } = node;
				if (!localId || registeredLocalIds.current.has(localId)) continue;
				registeredLocalIds.current.add(localId);
				ctx.markPresentAtLoad(localId);

				if (value) {
					ctx.register(localId, value);
				} else if (error) {
					ctx.registerError(localId, error.code);
				}
			}
		}

		if (staticMacrosComplete && !fullyLoadedRef.current) {
			fullyLoadedRef.current = true;
			ctx.markFullyLoaded();
		}
	}, [ctx, staticMacroData, staticMacrosComplete, contentId, expectedContentId]);

	return null;
}

export function StaticMacrosBridge({
	children = null,
	draftSyncCompletedRef,
	requestDraftSync,
	contentId,
}: {
	children?: React.ReactNode;
	draftSyncCompletedRef?: React.MutableRefObject<DraftSyncCompletedHandler | null>;
	requestDraftSync?: () => void;
	/** Pass on surfaces that navigate between content without remounting, such as the view page. */
	contentId?: string;
}): React.ReactNode {
	// Always render the provider so the tree shape is stable between SSR and the first client render.
	return (
		<StaticMacroStoreProvider contentId={contentId}>
			{/* eslint-disable-next-line confluence-feature-gating/no-new-feature-gates -- Existing gate retained during CCP-9677 lint-rule rollout; migration deferred. */}
			{fg('conf_frontend_forge_static_macro_mvp') && (
				<StaticMacroDataBridge
					draftSyncCompletedRef={draftSyncCompletedRef}
					requestDraftSync={requestDraftSync}
					expectedContentId={contentId}
				/>
			)}
			{children}
		</StaticMacroStoreProvider>
	);
}
